import PropTypes from 'prop-types';

import { useState } from 'react';

import { Button, Table, Modal, ModalDialog, Box, ModalClose, Avatar, Switch } from '@mui/joy'

import { InputField, InputFieldFrame } from './InputField';
import { SearchNodeList } from './SearchNodeList';

import i18n from '@/i18n'

import { Img } from './Img';

import { isSubject } from '@/utils';
import { useEffect } from 'react';
import { Typography } from '@mui/material';
import { HBox } from './HBox';
import { PlayButton } from './PlayButton';
import { PlayCircle } from '@phosphor-icons/react';

export function NodeCell({ column, node, readOnly }) {
  console.log(node)
  switch (column.type) {
  case "belongsTo":
    return (
      node[column.id] instanceof Array ? (
        node[column.id].map(n => n.name).join(', ')
      ) : node[column.id] instanceof Object ? (
        node[column.id].name
      ) : ''
    )
  case "belongsToMany":
    return (
      node[column.id] instanceof Array ? (
        node[column.id].map(n => {
          if (column.render instanceof Function) {
            return column.render({ value: n, node })
          } else {
            return n.name
          }
        })
      ) : node[column.id] instanceof Object ? (
        column.render instanceof Function? column.render({ node, value: node[column.id] }) : node[column.id].name
      ) : ''
    )
  case "percentage":
    return (
      <Typography sx={{ textAlign: 'right', marginRight: 20 }}>{(node[column.id] ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</Typography>
    )
  case "bool":
    return (
      <HBox>
        <Switch readOnly={readOnly} defaultChecked={node[column.id]} /> {node[column.id] ? (column.onLabel ?? column.label) : (column.offLabel ?? column.label)}
      </HBox>
    )
  case "custom":
    return column.render({ node, column, value: node[column.id] })
  default:
    return (
      node[column.id] instanceof Array ? (
        node[column.id].map(n => n.name).join(', ')
      ) : node[column.id] instanceof Object ? (
        node[column.id].name
      ) : node[column.id]
    )
  }
}

NodeCell.propTypes = {
  column: PropTypes.object,
  node: PropTypes.object,
  readOnly: PropTypes.bool
}

export function SelectRelatedNodes({
  label,
  createNodeColumns,
  createLabel,
  allLabel,
  addLabel,
  ownLabel,
  showIndexes = false,
  placeholder,
  errors,
  canCreate=false,
  hasPlayer=false,
  nodes,
  columns = [],
  emptyText,
  junctionNodeColumns = [],
  showName = true,
  onChange,
  readOnly,
  junctionNodeType,
  nodeType
}) {
  const [isAddingNodes, setAddingNodes] = useState(false)
  const [selectedNodes, setSelectedNodes] = useState(nodes)
  
  useEffect(() => {
    setSelectedNodes(nodes)
  }, [nodes])
  const handleChange = (nodes) => {
    setSelectedNodes(nodes)
  }

  const handleSubmitSetNodes = (selectedNodes) => {
    onChange(selectedNodes)
    setAddingNodes(false)
  }

  return (
    <>
      <InputField
        label={label}
        errors={errors}
      >
        <InputFieldFrame sx={{  padding: '0' }}>
          <Box
            sx={{
              overflowY: 'scroll',
              alignSelf: 'stretch',
              borderRight: '2pt solid rgba(127, 127, 127, .5)',
              flex: 1
            }}
          >
            <Table
              sx={{ height: '100%', width: '100%', 'td': { p: 1} }}
            >
              <thead>
                <tr>
                  {showIndexes && (<th style={{ width: 30 }}>#</th>)}
                  {hasPlayer && <th style={{ width: 50 }}><PlayCircle size={10} /></th>}
                  <th style={{ width: 50 }}></th>
                  {showName && <th>{i18n.t('name')}</th>}
                  {columns?.map(column => <th key={column.id}>{column.name ?? column.id}</th>)}
                  {junctionNodeColumns.map(column => <th key={column.id}>{column.name ?? column.id}</th>)}
                </tr>
              </thead>
              <tbody style={{ height: '100%',  overflowY: 'scroll' }}>
                {selectedNodes?.filter(n => !n.deleted).length > 0 ?
                  <>
                    {selectedNodes?.filter(n => !n.deleted).map((node, i) => (
                      <tr className={node?.deleted ? 'deleted' : ''} key={node.id}>
                        {showIndexes && (
                          <>
                            <td style={{ textAlign: 'right', opacity: 0.5, width: 40, padding: 8 }}>
                              <Typography sx={{ fontSize: 20}}>{(i + 1).toLocaleString().padStart(2, '0')}</Typography>
                            </td>
                          </>
                        )}
                        {hasPlayer && <td><PlayButton recording={node?.recording} /></td>}
                        <td style={{ width: 10 }}>
                          {isSubject(nodeType) ?
                            <Avatar src={node[nodeType].thumbnail_url ?? node[nodeType].image_url} size="sm" style={{ marginRight: 1 }} />
                          :
                            <Img sx={{ width: 30, objectFit: 'contain' }} src={node[nodeType].thumbnail_url ?? node[nodeType].image_url} style={{ height: '30pt', marginRight: 1 }} />
                          }
                        </td>
                        {showName && <td>{node[nodeType]?.name}</td>}
                        {columns?.map(column => <td key={column.id}><NodeCell column={column} node={node[nodeType]} /></td>)}
                        {junctionNodeColumns.map(column => <td key={column.id}><NodeCell column={column} node={node} /></td>)}
                      </tr>
                    ))}
                  </>
                :
                  <tr>
                    <td colSpan={2}>
                      <Typography sx={{ fontFamily: "DM Mono" }}>{placeholder}</Typography>
                    </td>
                  </tr>
                }
              </tbody>
            </Table>
          </Box>
          {!readOnly && <Box sx={{ p: 2, display: 'flex', alignSelf: 'center', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              aria-label={nodes?.length > 0 ? 'Edit' : 'Add'}
              variant="outlined"
              color="primary"
              onClick={() => setAddingNodes(true)}
            >
              {i18n.t(nodes?.length > 0 ? 'edit' : 'add')}
            </Button>
          </Box>}
        </InputFieldFrame>
      </InputField>
      <Modal open={isAddingNodes} onClose={() => setAddingNodes(false)}>
        <ModalDialog>
          <ModalClose />
          <SearchNodeList
            showName={showName}
            createLabel={createLabel}
            columns={columns}
            createNodeColumns={createNodeColumns}
            junctionNodeColumns={junctionNodeColumns}
            junctionNodeType={junctionNodeType}
            nodeType={nodeType}
            onClose={() => setAddingNodes(false)}
            emptyText={emptyText}
            isSubject={isSubject}
            canCreate={canCreate}
            showIndexes={showIndexes}
            selectedNodes={selectedNodes}
            allLabel={allLabel}
            addLabel={addLabel}
            ownLabel={ownLabel}
            onChange={handleChange}
            onSubmit={handleSubmitSetNodes}
          />
        </ModalDialog>
      </Modal>
    </>
  )
}

SelectRelatedNodes.propTypes = {
  label: PropTypes.string,
  createNodeColumns: PropTypes.array,
  heading: PropTypes.string,
  createLabel: PropTypes.string,
  allLabel: PropTypes.string,
  addLabel: PropTypes.string,
  ownLabel: PropTypes.string,
  showIndexes: PropTypes.bool,
  placeholder: PropTypes.string,
  errors: PropTypes.array,
  canCreate: PropTypes.bool,
  nodes: PropTypes.array,
  columns: PropTypes.array,
  emptyText: PropTypes.string,
  junctionNodeColumns: PropTypes.array,
  showName: PropTypes.bool,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  junctionNodeType: PropTypes.string,
  nodeType: PropTypes.string,
  ownerNodeType: PropTypes.string,
  ownerNodeId: PropTypes.string,
  hasPlayer: PropTypes.bool
}
